import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// 커스텀 락: 여러 탭에서 동시 접근할 때 교착(deadlock)을 막기 위해 타임아웃 상한을 둠
async function lockWithTimeout(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<unknown>,
): Promise<unknown> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn();
  }
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;

    const timer = setTimeout(() => {
      // 락을 일정 시간 안에 못 얻으면 실패시키지 않고, 락 없이 바로 실행해
      // 로그인/세션 로직이 멈추지 않도록 함 (흰 화면 방지)
      controller.abort();
      if (!settled) {
        settled = true;
        fn().then(resolve).catch(reject);
      }
    }, acquireTimeout || 10000);

    navigator.locks
      .request(name, { signal: controller.signal }, async () => {
        clearTimeout(timer);
        if (settled) return;
        settled = true;
        try {
          resolve(await fn());
        } catch (e) {
          reject(e);
        }
      })
      .catch((e) => {
        clearTimeout(timer);
        if (!settled) {
          settled = true;
          reject(e);
        }
      });
  });
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: lockWithTimeout,
  },
});

// 운영자 황명철 전용 계정 식별자 (학원별 상세 보기 권한)
export const MASTER_OPERATOR_ID = 'fcb9614c-dac6-476b-b07c-6a9412a0296d';

export interface Profile {
  id: string;
  full_name: string;
  role: string;
  status: string;
  academy_id: string | null;
  academy_name: string | null;
  address: string | null;
  phone: string | null;
  city: string | null;
  district: string | null;
  grade: string | null;
  subscription_plan: string | null;
  subscription_expires_at: string | null;
  created_at: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  role: string;
  plan: string;
  price: number;
  status: string;
  academy_id: string | null;
  created_at: string;
  activated_at: string | null;
  expires_at: string | null;
  reviewed_at: string | null;
  profiles?: { full_name: string | null; phone: string | null; academy_name: string | null } | null;
}

export interface Student {
  id: string;
  name: string;
  birth_date: string | null;
  school: string | null;
  grade: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  notes: string | null;
  academy_id: string | null;
  login_id: string | null;
  user_id: string | null;
  created_at: string;
}

export interface CapacityRequest {
  id: string;
  academy_id: string;
  requested_slots: number;
  status: string;
  created_at: string;
  reviewed_at: string | null;
}

export interface RefundRequest {
  id: string;
  user_id: string;
  subscription_id: string;
  role: string;
  plan: string;
  paid_price: number;
  refund_amount: number;
  status: string;
  reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export interface Inquiry {
  id: string;
  user_id: string;
  email: string | null;
  category: string;
  title: string;
  content: string;
  status: string;
  response: string | null;
  responded_at: string | null;
  created_at: string;
}

export interface TestResult {
  id: string;
  student_id: string | null;
  user_id: string | null;
  academy_id: string | null;
  test_type: string;
  title: string | null;
  score: number | null;
  result: Record<string, unknown> | null;
  created_at: string;
}